/**
 * 품의(·원인행위·결의) 소득명세 등록 팝업
 * - 기타/사업소득자 불러오기 + 지급액·세율 입력 → 원천세 산출
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import DraggableDialog from 'components/common/DraggableDialog';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { ActionButton } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  INCOME_KIND,
  INCOME_KIND_OPTIONS,
  INCOME_KIND_DEFAULTS,
  ID_TYPE_OPTIONS,
  INCOME_REF_STAGE,
  createStatementUid,
  emptyIncomeStatement,
  calcIncomeTaxFields,
  loadStatementsByProposal,
  saveStatementsForProposal,
  loadStatementsByResolution,
  saveStatementsForResolution,
  loadStatementsByDraft,
  saveStatementsForDraft,
  ensureIncomeEarnerSeed,
  fetchIncomeEarners,
  filterIncomeEarners,
  loadIncomeEarners,
  statementFromEarner,
  incomeKindLabel,
  idTypeLabel,
} from 'expense/expenseIncomeStore';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  min-height: 0;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  flex: 1;
  min-width: 160px;
`;

const GridWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 4px;
  border-top: 1px solid ${theme.colors.border};
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.primary};
  padding: 2px 0;
  flex-shrink: 0;
`;

function formatAmt(n) {
  return Number(n || 0).toLocaleString();
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {{ tenantCode: string, fiscalYear: string }} props.dims
 * @param {string} [props.proposalUid] 품의 모드
 * @param {string} [props.proposalNo]
 * @param {string|number} [props.proposalAmt]
 * @param {string} [props.resolutionUid] 결의 모드 (지출결의등록 type1 등)
 * @param {string} [props.resolutionNo]
 * @param {string|number} [props.resolutionAmt]
 * @param {string} [props.draftSessionUid] type1 임시연결 모드 (미채번)
 * @param {(rows: object[]) => void} [props.onSaved] 저장 후 콜백(채주 반영 등)
 * @param {boolean} [props.readOnly]
 * @param {string} [props.prefsUserId]
 */
export default function IncomeStatementDialog({
  open,
  onClose,
  dims,
  proposalUid,
  proposalNo = '',
  proposalAmt = 0,
  resolutionUid = '',
  resolutionNo = '',
  resolutionAmt = 0,
  draftSessionUid = '',
  onSaved,
  readOnly = false,
  prefsUserId = '',
}) {
  const gridRef = useRef(null);
  const [earnerLookupOpen, setEarnerLookupOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [sumPay, setSumPay] = useState(0);

  const canEdit = !readOnly;
  const isDraftMode = !!String(draftSessionUid || '').trim();
  const isResolutionMode = !isDraftMode && !!String(resolutionUid || '').trim();
  const docUid = isDraftMode
    ? String(draftSessionUid || '').trim()
    : isResolutionMode
      ? String(resolutionUid || '').trim()
      : String(proposalUid || '').trim();
  const docRefStage =
    isDraftMode || isResolutionMode ? INCOME_REF_STAGE.RESOLUTION : INCOME_REF_STAGE.PROPOSAL;
  const docAmt = isDraftMode || isResolutionMode ? resolutionAmt : proposalAmt;

  const refreshSummary = useCallback(() => {
    const rows = (gridRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
    setRowCount(rows.length);
    setSumPay(rows.reduce((s, r) => s + (Number(r.payAmt) || 0), 0));
  }, []);

  const applyCalcToRow = useCallback((row) => {
    if (!row) return row;
    const kind = row.incomeKind || INCOME_KIND.OTHER;
    const defaults = INCOME_KIND_DEFAULTS[kind] || INCOME_KIND_DEFAULTS[INCOME_KIND.OTHER];
    const next = {
      ...row,
      expenseRate: row.expenseRate ?? defaults.expenseRate,
      taxRate: row.taxRate ?? defaults.taxRate,
    };
    return { ...next, ...calcIncomeTaxFields(next) };
  }, []);

  const columns = useMemo(() => {
    if (!canEdit) {
      return [
        hiCol.roTextColumn({ header: '소득자코드', field: 'earnerCode', size: 90 }),
        hiCol.roTextColumn({ header: '소득자명', field: 'earnerName', size: 100 }),
        hiCol.roTextColumn({ header: '소득구분', field: 'incomeKindNm', size: 90 }),
        hiCol.roTextColumn({ header: '식별유형', field: 'idTypeNm', size: 100 }),
        hiCol.roTextColumn({ header: '주민/사업자번호', field: 'idNo', size: 130 }),
        hiCol.roNumberColumn({ header: '지급액', field: 'payAmt', size: 100, align: 'right' }),
        hiCol.roNumberColumn({ header: '필요경비율%', field: 'expenseRate', size: 90, align: 'right' }),
        hiCol.roNumberColumn({ header: '필요경비', field: 'expenseAmt', size: 90, align: 'right' }),
        hiCol.roNumberColumn({ header: '과세표준', field: 'taxableAmt', size: 90, align: 'right' }),
        hiCol.roNumberColumn({ header: '소득세율%', field: 'taxRate', size: 80, align: 'right' }),
        hiCol.roNumberColumn({ header: '소득세', field: 'incomeTax', size: 90, align: 'right' }),
        hiCol.roNumberColumn({ header: '지방소득세', field: 'localTax', size: 90, align: 'right' }),
        hiCol.roNumberColumn({ header: '실지급액', field: 'netPayAmt', size: 100, align: 'right' }),
        hiCol.roTextColumn({ header: '은행', field: 'bankName', size: 90 }),
        hiCol.roTextColumn({ header: '계좌번호', field: 'accountNo', size: 120 }),
        hiCol.roTextColumn({ header: '비고', field: 'remark', size: 140 }),
      ];
    }
    return [
      hiCol.edTextColumn({ header: '소득자코드', field: 'earnerCode', size: 90 }),
      hiCol.edTextColumn({ header: '소득자명', field: 'earnerName', size: 100 }),
      hiCol.edComboColumn({
        header: '소득구분',
        field: 'incomeKind',
        size: 90,
        options: INCOME_KIND_OPTIONS,
      }),
      hiCol.edComboColumn({
        header: '식별유형',
        field: 'idType',
        size: 100,
        options: ID_TYPE_OPTIONS,
      }),
      hiCol.edTextColumn({ header: '주민/사업자번호', field: 'idNo', size: 130 }),
      hiCol.edNumberColumn({ header: '지급액', field: 'payAmt', size: 100, align: 'right' }),
      hiCol.edNumberColumn({ header: '필요경비율%', field: 'expenseRate', size: 90, align: 'right' }),
      hiCol.roNumberColumn({ header: '필요경비', field: 'expenseAmt', size: 90, align: 'right' }),
      hiCol.roNumberColumn({ header: '과세표준', field: 'taxableAmt', size: 90, align: 'right' }),
      hiCol.edNumberColumn({ header: '소득세율%', field: 'taxRate', size: 80, align: 'right' }),
      hiCol.roNumberColumn({ header: '소득세', field: 'incomeTax', size: 90, align: 'right' }),
      hiCol.roNumberColumn({ header: '지방소득세', field: 'localTax', size: 90, align: 'right' }),
      hiCol.roNumberColumn({ header: '실지급액', field: 'netPayAmt', size: 100, align: 'right' }),
      hiCol.edTextColumn({ header: '은행', field: 'bankName', size: 90 }),
      hiCol.edTextColumn({ header: '계좌번호', field: 'accountNo', size: 120 }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 140 }),
    ];
  }, [canEdit]);

  const grid = useHiGridHook({
    gridRef,
    autoSearch: false,
    enableContextMenu: canEdit,
    confirmCheck: canEdit,
    defaultRow: emptyIncomeStatement({
      refStage: docRefStage,
      proposalUid: isResolutionMode || isDraftMode ? '' : proposalUid,
      resolutionUid: isResolutionMode ? resolutionUid : '',
      resolutionNo: isResolutionMode ? resolutionNo : '',
      draftSessionUid: isDraftMode ? draftSessionUid : '',
      fiscalYear: dims?.fiscalYear || '',
    }),
  });

  const loadRows = useCallback(async () => {
    if (!open || !dims?.tenantCode || !dims?.fiscalYear || !docUid) {
      gridRef.current?.setData?.([]);
      setRowCount(0);
      setSumPay(0);
      return;
    }
    ensureIncomeEarnerSeed({ tenantCode: dims.tenantCode });
    await fetchIncomeEarners({ tenantCode: dims.tenantCode });
    await fetchIncomeEarners({ tenantCode: dims.tenantCode });
    const loaded = isDraftMode
      ? loadStatementsByDraft(dims, docUid)
      : isResolutionMode
        ? loadStatementsByResolution(dims, docUid)
        : loadStatementsByProposal(dims, docUid);
    const rows = loaded.map((r) => ({
      ...r,
      incomeKindNm: incomeKindLabel(r.incomeKind),
      idTypeNm: idTypeLabel(r.idType),
    }));
    gridRef.current?.setData?.(rows);
    setRowCount(rows.length);
    setSumPay(rows.reduce((s, r) => s + (Number(r.payAmt) || 0), 0));
  }, [open, dims, docUid, isDraftMode, isResolutionMode]);

  useEffect(() => {
    if (!open) return;
    // 팝업 오픈 직후 그리드 마운트 대기
    const t = setTimeout(loadRows, 0);
    return () => clearTimeout(t);
  }, [open, loadRows]);

  // 지급액·세율·소득구분 변경 시 세금 재계산
  useEffect(() => {
    if (!open || !canEdit) return undefined;
    grid.onAfterSaveCell = (rowData, { field } = {}) => {
      if (!['payAmt', 'expenseRate', 'taxRate', 'incomeKind'].includes(field)) {
        refreshSummary();
        return;
      }
      const uid = rowData?.statementUid;
      if (!uid) return;
      let next = { ...rowData };
      if (field === 'incomeKind') {
        const defaults =
          INCOME_KIND_DEFAULTS[next.incomeKind] || INCOME_KIND_DEFAULTS[INCOME_KIND.OTHER];
        next.expenseRate = defaults.expenseRate;
        next.taxRate = defaults.taxRate;
      }
      next = applyCalcToRow(next);
      if (next.mod !== 'A') next.mod = 'U';
      gridRef.current?.setData?.((prev) =>
        (prev || []).map((r) => (r.statementUid === uid ? { ...r, ...next } : r))
      );
      refreshSummary();
    };
    return () => {
      grid.onAfterSaveCell = undefined;
    };
  }, [open, canEdit, grid, applyCalcToRow, refreshSummary]);

  const handleAdd = useCallback(() => {
    if (!canEdit) return;
    const row = emptyIncomeStatement({
      statementUid: createStatementUid(),
      proposalUid: isResolutionMode || isDraftMode ? '' : proposalUid,
      resolutionUid: isResolutionMode ? resolutionUid : '',
      resolutionNo: isResolutionMode ? resolutionNo : '',
      draftSessionUid: isDraftMode ? draftSessionUid : '',
      fiscalYear: dims?.fiscalYear || '',
      refStage: docRefStage,
      mod: 'A',
    });
    const rows = gridRef.current?.getData?.() || [];
    gridRef.current?.setData?.([...rows, row]);
    refreshSummary();
  }, [
    canEdit,
    isDraftMode,
    isResolutionMode,
    proposalUid,
    resolutionUid,
    resolutionNo,
    draftSessionUid,
    docRefStage,
    dims,
    refreshSummary,
  ]);

  const handleDelete = useCallback(async () => {
    if (!canEdit) return;
    await grid.handleDelete?.();
    refreshSummary();
  }, [canEdit, grid, refreshSummary]);

  const earnerLookupConfig = useMemo(() => {
    ensureIncomeEarnerSeed({ tenantCode: dims?.tenantCode });
    const rows = filterIncomeEarners(loadIncomeEarners({ tenantCode: dims?.tenantCode }), {
      useYn: 'Y',
    }).map((r) => ({
      ...r,
      incomeKindNm: incomeKindLabel(r.incomeKind),
      idTypeNm: idTypeLabel(r.idType),
    }));
    return {
      title: '소득자 조회',
      columns: [
        hiCol.roTextColumn({ header: '코드', field: 'earnerCode', size: 80 }),
        hiCol.roTextColumn({ header: '성명', field: 'earnerName', size: 100 }),
        hiCol.roTextColumn({ header: '소득구분', field: 'incomeKindNm', size: 80 }),
        hiCol.roTextColumn({ header: '식별유형', field: 'idTypeNm', size: 100 }),
        hiCol.roTextColumn({ header: '주민/사업자번호', field: 'idNo', size: 130 }),
        hiCol.roTextColumn({ header: '은행', field: 'bankName', size: 90 }),
        hiCol.roTextColumn({ header: '비고', field: 'remark', size: 160 }),
      ],
      rows,
      enableSearch: true,
      searchConfig: {
        mode: 'unified',
        keywordLabel: '성명/번호',
        keywordFields: ['earnerCode', 'earnerName', 'idNo', 'remark'],
        codeFields: ['earnerCode'],
        nameFields: ['earnerName'],
      },
    };
  }, [dims?.tenantCode, earnerLookupOpen]);

  const applyEarners = useCallback(
    (selected) => {
      const list = Array.isArray(selected) ? selected : selected ? [selected] : [];
      if (!list.length) return false;
      const existing = gridRef.current?.getData?.() || [];
      const added = list.map((earner) =>
        applyCalcToRow(
          statementFromEarner(earner, {
            statementUid: createStatementUid(),
            proposalUid: isResolutionMode || isDraftMode ? '' : proposalUid,
            resolutionUid: isResolutionMode ? resolutionUid : '',
            resolutionNo: isResolutionMode ? resolutionNo : '',
            draftSessionUid: isDraftMode ? draftSessionUid : '',
            fiscalYear: dims?.fiscalYear || '',
            refStage: docRefStage,
            mod: 'A',
          })
        )
      );
      gridRef.current?.setData?.([...existing, ...added]);
      refreshSummary();
      setEarnerLookupOpen(false);
      return true;
    },
    [
      isDraftMode,
      isResolutionMode,
      proposalUid,
      resolutionUid,
      resolutionNo,
      draftSessionUid,
      docRefStage,
      dims,
      applyCalcToRow,
      refreshSummary,
    ]
  );

  const handleSave = useCallback(async () => {
    if (!canEdit) return;
    if (!docUid) {
      showAlert(
        isDraftMode || isResolutionMode
          ? '결의 초안이 없습니다. 화면을 다시 열어주세요.'
          : '품의를 먼저 선택하거나 추가하세요.'
      );
      return;
    }
    if (!dims?.tenantCode || !dims?.fiscalYear) {
      showAlert('기관·회계연도가 없습니다.');
      return;
    }
    const raw = gridRef.current?.getData?.() || [];
    const rows = raw
      .filter((r) => r.mod !== 'D')
      .map((r) =>
        applyCalcToRow({
          ...r,
          statementUid: r.statementUid || createStatementUid(),
          proposalUid: isResolutionMode || isDraftMode ? r.proposalUid || '' : proposalUid,
          resolutionUid: isResolutionMode ? resolutionUid : '',
          resolutionNo: isResolutionMode ? resolutionNo : '',
          draftSessionUid: isDraftMode ? draftSessionUid : r.draftSessionUid || '',
          fiscalYear: dims.fiscalYear,
          refStage: docRefStage,
        })
      );

    for (const r of rows) {
      if (!String(r.earnerName || '').trim()) {
        showAlert('소득자명을 입력하세요.');
        return;
      }
      if (!Number(r.payAmt) || Number(r.payAmt) <= 0) {
        showAlert(`「${r.earnerName}」의 지급액을 입력하세요.`);
        return;
      }
    }

    if (isDraftMode) {
      saveStatementsForDraft(dims, draftSessionUid, rows);
    } else if (isResolutionMode) {
      saveStatementsForResolution(dims, resolutionUid, rows, resolutionNo);
    } else {
      saveStatementsForProposal(dims, proposalUid, rows);
    }
    showAlert(`소득명세 ${rows.length}건을 저장했습니다.`, 'success');
    onSaved?.(rows);
    loadRows();
  }, [
    canEdit,
    docUid,
    isDraftMode,
    isResolutionMode,
    proposalUid,
    resolutionUid,
    resolutionNo,
    draftSessionUid,
    docRefStage,
    dims,
    applyCalcToRow,
    loadRows,
    onSaved,
  ]);

  const handleClose = useCallback(async () => {
    if (canEdit) {
      const dirty = (gridRef.current?.getData?.() || []).some(
        (r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D'
      );
      if (dirty) {
        const ok = await showConfirm('저장하지 않은 변경이 있습니다. 닫을까요?');
        if (!ok) return;
      }
    }
    onClose?.();
  }, [canEdit, onClose]);

  const titleNo = isDraftMode
    ? ' · 임시연결'
    : isResolutionMode
      ? resolutionNo
        ? ` · 결의 ${resolutionNo}`
        : ' · 지출결의'
      : proposalNo
        ? ` · 품의 ${proposalNo}`
        : '';

  return (
    <>
      <DraggableDialog
        open={open}
        title={`소득명세 등록${titleNo}`}
        onClose={handleClose}
        dialogWidth="min(96vw, 1180px)"
        dialogHeight="min(88vh, 640px)"
        prefsUserId={prefsUserId}
        sizeKey={
          isDraftMode
            ? 'income-statement-draft'
            : isResolutionMode
              ? 'income-statement-resolution'
              : 'income-statement-proposal'
        }
        keepMounted={false}
      >
        <Body>
          <Toolbar>
            {canEdit && (
              <>
                <ActionButton type="button" onClick={() => setEarnerLookupOpen(true)}>
                  소득자 불러오기
                </ActionButton>
                <ActionButton type="button" onClick={handleAdd}>
                  행추가
                </ActionButton>
                <ActionButton type="button" $variant="danger" onClick={handleDelete}>
                  행삭제
                </ActionButton>
              </>
            )}
            <Hint>
              기타·사업소득자 정보와 지급액·세율을 입력하면 원천세·실지급액이 자동 산출됩니다.
              {readOnly ? ' (조회 전용)' : ''}
            </Hint>
          </Toolbar>

          <Summary>
            <span>명세 {rowCount}건</span>
            <span>지급합계 {formatAmt(sumPay)}원</span>
            <span>
              {isDraftMode || isResolutionMode ? '결의금액' : '품의금액'} {formatAmt(docAmt)}원
            </span>
          </Summary>

          <GridWrap>
            <HiGridComponent
              ref={gridRef}
              columns={columns}
              height="100%"
              showRowNumber
              rowNumberSize={48}
              enableColumnFilters
              enableContextMenu={canEdit}
              enableRowEditMenu={canEdit}
              enableCut={canEdit}
              enableFindReplace
              allowReplace={canEdit}
              enableHeaderMenu
              enableSelectionStats
              enableFreeze
              enableQuickExport
              exportFileName="소득명세"
              gridId="expense-income-statement"
              prefsUserId={prefsUserId}
              {...(canEdit ? grid.contextMenuProps || {} : {})}
            />
          </GridWrap>

          <FooterBar>
            <ActionButton type="button" onClick={handleClose}>
              닫기
            </ActionButton>
            {canEdit && (
              <ActionButton type="button" $variant="success" onClick={handleSave}>
                저장
              </ActionButton>
            )}
          </FooterBar>
        </Body>
      </DraggableDialog>

      <CodeLookupDialog
        open={earnerLookupOpen}
        title={earnerLookupConfig.title}
        columns={earnerLookupConfig.columns}
        rows={earnerLookupConfig.rows}
        enableSearch={earnerLookupConfig.enableSearch}
        searchConfig={earnerLookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey="codeLookup-income-earner"
        onSelect={applyEarners}
        onClose={() => setEarnerLookupOpen(false)}
      />
    </>
  );
}
